import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  CUSTOM_PRESETS_KEY,
  CustomPresetStorageError,
  createCustomPreset,
  deleteCustomPreset,
  listCustomPresets,
  newPresetId,
  renameCustomPreset,
  replaceAllCustomPresets,
  updateCustomPreset,
  type CustomPresetEntry,
} from './customPresets';
import { applyImport, buildImportOps, countUserSkips } from './customPresetIO';
import { CustomPresetsContext, type CustomPresetsValue } from './customPresetsContext';
import { track } from '../analytics/client';

/** Whether the runtime can mint a uuid — false in an insecure (non-https) context. */
function canMintId(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
}

/**
 * Holds the live custom-preset snapshot above the QC/Editor/Batch consumers
 * (cue-forge-3r9 §3), mounted between `SessionProvider` and `QcProvider`. It loads
 * `listCustomPresets()` once, then re-reads the store on every mutation and on a
 * cross-tab `storage` event, bumping `revision` and producing a fresh `presets`
 * array reference each time so `QcProvider`'s resolved-ruleset memo re-fires (the
 * §6 in-place-edit re-run). Writes propagate {@link CustomPresetStorageError} to
 * the caller so the editor can surface quota / soft-cap / non-secure-context
 * failures instead of losing the operator's work.
 */
export function CustomPresetsProvider({ children }: { children: ReactNode }) {
  const [presets, setPresets] = useState<readonly CustomPresetEntry[]>(() => listCustomPresets());
  const [revision, setRevision] = useState(0);

  const reload = useCallback(() => {
    setPresets(listCustomPresets());
    setRevision((r) => r + 1);
  }, []);

  // Cross-tab convergence: another tab's write fires a `storage` event here. A
  // null key (storage cleared) also refreshes.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === CUSTOM_PRESETS_KEY) reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [reload]);

  const save = useCallback<CustomPresetsValue['save']>(
    (ruleset, opts = {}) => {
      const { id, basedOn } = opts;
      // Update in place when the id still exists; otherwise fall through to a
      // fresh create (a new id, "save as new" — the entry was removed elsewhere).
      const updated = id ? updateCustomPreset(id, ruleset) : null;
      let entry: CustomPresetEntry;
      if (updated) {
        entry = updated;
      } else {
        if (!canMintId()) {
          throw new CustomPresetStorageError(
            'Saving a custom preset needs a secure (https) context.',
            false,
          );
        }
        entry = createCustomPreset(ruleset, basedOn ? { basedOn } : {});
        // A new preset was authored (from scratch or cloned) — never its name/id,
        // which can carry user free text; the empty-prop event is just a tally.
        track('custom_preset_created', {});
      }
      reload();
      return entry;
    },
    [reload],
  );

  const rename = useCallback<CustomPresetsValue['rename']>(
    (id, name) => {
      const entry = renameCustomPreset(id, name);
      if (entry) reload();
      return entry;
    },
    [reload],
  );

  const remove = useCallback<CustomPresetsValue['remove']>(
    (id) => {
      const removed = deleteCustomPreset(id);
      if (removed) reload();
      return removed;
    },
    [reload],
  );

  const importPresets = useCallback<CustomPresetsValue['importPresets']>(
    (items) => {
      // Any resolution that produces an *add* needs a freshly minted id — guard the
      // non-secure context up front so nothing is written on a partial failure.
      const needsMint = items.some(
        (it) => it.resolution !== 'skip' && !(it.resolution === 'overwrite' && it.conflict),
      );
      if (needsMint && !canMintId()) {
        throw new CustomPresetStorageError(
          'Importing a custom preset needs a secure (https) context.',
          false,
        );
      }
      const ops = buildImportOps(items, newPresetId, Date.now());
      const { next, summary } = applyImport(listCustomPresets(), ops);
      replaceAllCustomPresets(next);
      reload();
      // "Skip" resolutions never become ops, so count them here — the summary
      // must not silently drop the operator's own choices (cue-forge-drt.19).
      return { ...summary, skippedByUser: countUserSkips(items) };
    },
    [reload],
  );

  const get = useCallback<CustomPresetsValue['get']>(
    (id) => presets.find((p) => p.id === id),
    [presets],
  );

  const value = useMemo<CustomPresetsValue>(
    () => ({ presets, revision, save, rename, remove, importPresets, get, reload }),
    [presets, revision, save, rename, remove, importPresets, get, reload],
  );

  return <CustomPresetsContext.Provider value={value}>{children}</CustomPresetsContext.Provider>;
}
